import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Pencil, ChevronLeft, ChevronRight, Download, Loader } from 'lucide-react';
import Header from '../components/Header';
import { participationsAPI, festivalExpensesAPI, handleApiError } from '../utils/api';
import { saveBlob } from '../utils/downloadFile';

const formatEuro = (value) => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR');
  } catch {
    return dateStr;
  }
};

const PAGE_SIZE = 15;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatRoi = (netResult, buyInPaid) => {
  if (netResult === null || netResult === undefined || !buyInPaid || Number(buyInPaid) === 0) {
    return null;
  }
  const roi = (Number(netResult) / Number(buyInPaid)) * 100;
  return roi;
};

/**
 * Journal de bord en LECTURE SEULE des résultats réellement enregistrés.
 *
 * ⚠️ Cette page n'a pas de formulaire d'ajout/modification : toute saisie
 * de résultat se fait désormais sur "Mes participations"
 * (MyParticipationsPage), la seule source de vérité pour les résultats
 * (table tournament_participations). Avant septembre 2026 cette page avait
 * son propre formulaire ("Ajouter un résultat"), mais il n'était relié à
 * aucune sauvegarde réelle (voir l'historique git) : elle a été transformée
 * en simple vue de consultation pour éviter la confusion avec la vraie
 * fonctionnalité de saisie.
 */
const TournamentsResultsPage = () => {
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [festivalFilter, setFestivalFilter] = useState(null);
  const [festivalExpenses, setFestivalExpenses] = useState([]);
  const [showAllFestivals, setShowAllFestivals] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await participationsAPI.getMine();
        setParticipations(response.data || []);
        // Frais de festival (hôtel, transport...) : une erreur ne bloque pas la page.
        try {
          const expensesResponse = await festivalExpensesAPI.getMine();
          setFestivalExpenses(expensesResponse.data || []);
        } catch (expErr) {
          console.error('Erreur chargement frais de festival', expErr);
        }
      } catch (err) {
        const { error: errTitle, details } = handleApiError(err);
        setError(`${errTitle} : ${details}`);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // Seuls les tournois pour lesquels un résultat a effectivement été saisi
  // (position ou gain renseigné) apparaissent dans le journal de bord —
  // une simple inscription sans résultat n'est pas "un résultat".
  //
  // Depuis le passage au résultat de série (voir MyParticipationsPage), un
  // day membre d'une série n'a plus de finishPosition/prizeWon propres :
  // son résultat (seriesFinishPosition/seriesPrizeWon, identique sur
  // chaque day de la série) est donc regroupé ici en UNE seule ligne par
  // série plutôt que répété (ou perdu) sur chaque day.
  //
  // Seules les participations TERMINÉES sont listées : un résultat n'a de
  // sens qu'une fois le tournoi joué (date <= aujourd'hui ; pour une série,
  // date du dernier day). L'API omet les champs vides (undefined, pas null) :
  // on teste donc avec "!= null" pour couvrir les deux cas.
  const allResults = useMemo(() => {
    const today = todayStr();
    const isPlayed = (date) => !!date && String(date).slice(0, 10) <= today;
    const active = participations.filter((p) => p.status !== 'CANCELLED');

    const standaloneRows = active
      .filter((p) => !p.seriesId && (p.finishPosition != null || p.prizeWon != null) && isPlayed(p.tournamentDate))
      .map((p) => ({
        key: `p-${p.id}`,
        tournamentDate: p.tournamentDate,
        festivalId: p.festivalId,
        festivalTitle: p.festivalTitle,
        totalExpenses: Number(p.totalExpenses) || 0,
        tournamentTitle: p.tournamentTitle,
        buyInPaid: p.buyInPaid,
        finishPosition: p.finishPosition,
        totalEntries: p.totalEntries,
        prizeWon: p.prizeWon,
        netResult: p.netResult,
      }));

    const seriesGroups = new Map();
    active
      .filter((p) => p.seriesId)
      .forEach((p) => {
        if (!seriesGroups.has(p.seriesId)) seriesGroups.set(p.seriesId, []);
        seriesGroups.get(p.seriesId).push(p);
      });

    const seriesRows = Array.from(seriesGroups.values())
      .filter((members) => members[0].seriesFinishPosition != null || members[0].seriesPrizeWon != null)
      .filter((members) => isPlayed(members.reduce((l, m) => (m.tournamentDate && m.tournamentDate > l ? m.tournamentDate : l), '')))
      .map((members) => {
        const buyInPaid = members.reduce((sum, m) => sum + (Number(m.buyInPaid) || 0), 0);
        const totalExpenses = members.reduce((sum, m) => sum + (Number(m.totalExpenses) || 0), 0);
        const prizeWon = members[0].seriesPrizeWon;
        const netResult = (Number(prizeWon) || 0) - buyInPaid - totalExpenses;
        const latestDate = members.reduce(
          (latest, m) => (!latest || (m.tournamentDate && m.tournamentDate > latest) ? m.tournamentDate : latest),
          null
        );
        return {
          key: `series-${members[0].seriesId}`,
          tournamentDate: latestDate,
          festivalId: members[0].festivalId,
          festivalTitle: members[0].festivalTitle,
          totalExpenses,
          tournamentTitle: `${members[0].seriesName} (série)`,
          buyInPaid,
          finishPosition: members[0].seriesFinishPosition,
          totalEntries: members[0].seriesTotalEntries,
          prizeWon,
          netResult,
        };
      });

    return [...standaloneRows, ...seriesRows].sort(
      (a, b) => new Date(b.tournamentDate || 0) - new Date(a.tournamentDate || 0)
    );
  }, [participations]);

  // Bilan par festival (sur tous les résultats, pas seulement ceux filtrés).
  const festivalSummaries = useMemo(() => {
    const map = new Map();
    const entryFor = (key, title) => {
      if (!map.has(key)) {
        map.set(key, {
          key,
          festivalId: typeof key === 'number' ? key : null,
          festivalTitle: title || 'Sans festival',
          lastDate: '',
          tournaments: 0,
          buyIn: 0,
          expenses: 0,
          cashout: 0,
          net: 0,
          itm: 0,
        });
      }
      return map.get(key);
    };
    allResults.forEach((r) => {
      const f = entryFor(r.festivalId ?? r.festivalTitle ?? 'inconnu', r.festivalTitle);
      f.tournaments += 1;
      f.buyIn += Number(r.buyInPaid) || 0;
      f.expenses += Number(r.totalExpenses) || 0;
      f.cashout += Number(r.prizeWon) || 0;
      f.net += Number(r.netResult) || 0;
      if (Number(r.prizeWon) > 0) f.itm += 1;
      const d = String(r.tournamentDate || '').slice(0, 10);
      if (d > f.lastDate) f.lastDate = d;
    });
    // Frais de festival : ajoutés au poste Frais et retranchés du bilan, y
    // compris pour un festival sans résultat saisi (ligne à 0 tournoi).
    festivalExpenses.forEach((e) => {
      const f = entryFor(e.festivalId, e.festivalTitle);
      const amount = Number(e.amount) || 0;
      f.expenses += amount;
      f.net -= amount;
      const d = String(e.expenseDate || '').slice(0, 10);
      if (d > f.lastDate) f.lastDate = d;
    });
    return Array.from(map.values()).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  }, [allResults, festivalExpenses]);

  // Un clic sur un festival du bilan filtre le journal sur ce festival.
  const results = useMemo(
    () => (festivalFilter == null
      ? allResults
      : allResults.filter((r) => (r.festivalId ?? r.festivalTitle ?? 'inconnu') === festivalFilter)),
    [allResults, festivalFilter]
  );
  const activeFestival = festivalSummaries.find((f) => f.key === festivalFilter) || null;

  // Pagination : retour à la première page si le nombre de lignes change,
  // et page courante bornée (ex. après un rechargement avec moins de lignes).
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedResults = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = useMemo(() => {
    const tournamentsJoues = allResults.length;
    const totalGains = allResults.reduce((sum, p) => sum + (Number(p.prizeWon) || 0), 0);
    const totalBuyIn = allResults.reduce((sum, p) => sum + (Number(p.buyInPaid) || 0), 0);
    const festivalExpensesTotal = festivalExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const totalNet = allResults.reduce((sum, p) => sum + (Number(p.netResult) || 0), 0) - festivalExpensesTotal;
    const itm = allResults.filter((p) => p.prizeWon && Number(p.prizeWon) > 0).length;
    const roi = totalBuyIn > 0 ? (totalNet / totalBuyIn) * 100 : null;

    return { tournamentsJoues, totalGains, totalBuyIn, totalNet, itm, roi };
  }, [allResults, festivalExpenses]);

  // Archive ZIP des justificatifs d'un festival (frais de festival + tournois).
  const handleExport = async (festivalId) => {
    setExportingId(festivalId);
    try {
      const { blob, fileName } = await festivalExpensesAPI.exportReceipts(festivalId);
      saveBlob(blob, fileName);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Export impossible : ${errTitle} - ${details}`);
    } finally {
      setExportingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="p-8 text-center text-gray-500">Chargement de tes résultats...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900">
              Mes Résultats de Tournois de Poker Live
            </h1>
            <button
              onClick={() => navigate('/my-participations')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center gap-2"
              title="La saisie et la modification des résultats se font sur Mes participations"
            >
              <Trophy size={20} />
              Saisir un résultat
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {/* Stats principales, calculées à partir des résultats réellement saisis */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-4xl font-bold text-gray-900 mb-2">{stats.tournamentsJoues}</div>
              <div className="text-gray-600">Tournois avec résultat</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-4xl font-bold text-gray-900 mb-2">{formatEuro(stats.totalGains)}</div>
              <div className="text-gray-600">Gains totaux</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className={`text-4xl font-bold mb-2 ${stats.totalNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatEuro(stats.totalNet)}
              </div>
              <div className="text-gray-600">Bilan net</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-4xl font-bold text-gray-900 mb-2">{stats.itm}</div>
              <div className="text-gray-600">ITM (gains &gt; 0)</div>
            </div>
          </div>

          {/* Bilan par festival */}
          {festivalSummaries.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Bilan par festival</h2>
              <p className="text-sm text-gray-500 mb-4">
                Les frais de festival (hôtel, transport, repas…) saisis sur « Mes participations » sont inclus dans « Frais » et dans le bilan. Clique sur un festival pour filtrer le journal ci-dessous.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">Festival</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Tournois</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Buy-in</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Frais</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Cashout</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">Bilan</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold text-gray-700">ROI</th>
                      <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700" title="Justificatifs du festival (ZIP)">Justif.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllFestivals ? festivalSummaries : festivalSummaries.slice(0, 8)).map((f) => {
                      const roi = f.buyIn > 0 ? (f.net / f.buyIn) * 100 : null;
                      const selected = festivalFilter === f.key;
                      return (
                        <tr
                          key={f.key}
                          onClick={() => { setFestivalFilter(selected ? null : f.key); setPage(1); }}
                          className={`border-b border-gray-100 cursor-pointer transition-colors ${
                            selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="py-2 px-3 text-sm text-gray-900 font-medium">{f.festivalTitle}</td>
                          <td className="py-2 px-3 text-sm text-gray-700 text-right">
                            {f.tournaments}
                            {f.itm > 0 && <span className="text-gray-400"> ({f.itm} ITM)</span>}
                          </td>
                          <td className="py-2 px-3 text-sm text-gray-900 text-right">{formatEuro(f.buyIn)}</td>
                          <td className="py-2 px-3 text-sm text-gray-900 text-right">{formatEuro(f.expenses)}</td>
                          <td className="py-2 px-3 text-sm text-gray-900 text-right">{formatEuro(f.cashout)}</td>
                          <td className={`py-2 px-3 text-sm text-right font-semibold ${
                            f.net >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {f.net > 0 ? '+' : ''}{formatEuro(f.net)}
                          </td>
                          <td className={`py-2 px-3 text-sm text-right font-semibold ${
                            roi === null ? 'text-gray-400' : roi >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {roi === null ? '—' : `${roi >= 0 ? '+' : ''}${roi.toFixed(0)}%`}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {f.festivalId != null && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleExport(f.festivalId); }}
                                disabled={exportingId === f.festivalId}
                                className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded disabled:opacity-50"
                                title="Télécharger les justificatifs de ce festival (ZIP)"
                                aria-label="Télécharger les justificatifs de ce festival"
                              >
                                {exportingId === f.festivalId ? (
                                  <Loader size={16} className="animate-spin" />
                                ) : (
                                  <Download size={16} />
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {festivalSummaries.length > 8 && (
                <button
                  onClick={() => setShowAllFestivals((v) => !v)}
                  className="mt-3 text-sm text-blue-600 hover:underline font-medium"
                >
                  {showAllFestivals ? 'Voir moins' : `Voir les ${festivalSummaries.length - 8} autres festivals`}
                </button>
              )}
            </div>
          )}

          {/* Journal de bord détaillé */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Journal de bord détaillé</h2>
            <p className="text-sm text-gray-500 mb-6">
              Tournois terminés dont le résultat est saisi via « Mes participations ». Pour ajouter
              ou corriger un résultat, va sur « Mes participations ».
            </p>

            {activeFestival && (
              <div className="mb-4 inline-flex items-center gap-2 bg-blue-50 text-blue-800 text-sm rounded-full px-3 py-1">
                Festival : <strong>{activeFestival.festivalTitle}</strong>
                <button
                  onClick={() => { setFestivalFilter(null); setPage(1); }}
                  className="hover:text-blue-950 font-bold"
                  aria-label="Retirer le filtre"
                >
                  ×
                </button>
              </div>
            )}

            {results.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Aucun résultat saisi pour l'instant.{' '}
                <button
                  onClick={() => navigate('/my-participations')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Saisir mon premier résultat
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Festival</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tournoi</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Buy-in</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Position</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gain</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Bilan</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">ROI</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedResults.map((p) => {
                      const roi = formatRoi(p.netResult, p.buyInPaid);
                      return (
                        <tr key={p.key} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-900">{formatDate(p.tournamentDate)}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{p.festivalTitle || '—'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{p.tournamentTitle}</td>
                          <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">
                            {formatEuro(p.buyInPaid)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700 text-right">
                            {p.finishPosition ?? '—'}
                            {p.totalEntries ? ` / ${p.totalEntries}` : ''}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">
                            {formatEuro(p.prizeWon)}
                          </td>
                          <td className={`py-3 px-4 text-sm text-right font-semibold ${
                            p.netResult >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatEuro(p.netResult)}
                          </td>
                          <td className={`py-3 px-4 text-sm text-right font-semibold ${
                            roi === null ? 'text-gray-400' : roi >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {roi === null ? '—' : `${roi >= 0 ? '+' : ''}${roi.toFixed(0)}%`}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => navigate('/my-participations')}
                              className="text-blue-600 hover:text-blue-800 transition p-2 hover:bg-blue-50 rounded"
                              title="Modifier sur Mes participations"
                            >
                              <Pencil size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {results.length > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
                <span className="text-sm text-gray-500">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, results.length)} sur {results.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Page précédente"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Page suivante"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentsResultsPage;
